// Copyright (c) Docugami, Inc. All rights reserved.

import { ISlotItem, ReportChunkSlotItem, SlotItemType } from "components/cards/ItemSlot";
import { IReportEntryItem } from "components/cards/ReportItemCardlet";
import { ReportBinding } from "networking/reportsApi";
import { useMemo } from "react";
import { AppDispatch, AppState } from "store";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { selectReportEntryBindingsForCurrentDocument } from "store/reports/selectors";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { useDocumentDgml } from "./documentDgml";
import { useAppDispatch, useAppSelector } from "./redux";

interface SlotForReportItemProps {
    item: IReportEntryItem
}

export interface ReportItemSlots {
    slots: ISlotItem[];
    isEmpty: boolean;
}

export function useReportEntryItemSlots(props: SlotForReportItemProps): ReportItemSlots {
    const { entry, isLinkable, isDisabled, isSelected } = props.item;
    const entryBindings = useAppSelector(state => selectReportEntryBindingsForCurrentDocument(state)?.entities[entry.id]);
    const dgml = useDocumentDgml();
    const isInitialized = dgml.isInitialized();
    const dispatch = useAppDispatch();

    const slots = useMemo((): ReportChunkSlotItem[] => {
        function createSlotForBinding(binding?: ReportBinding): ReportChunkSlotItem {
            const instanceId = binding?.spanId;

            const item: ReportChunkSlotItem = {
                id: instanceId ?? entry.id,
                type: SlotItemType.ReportChunk,
                isDisabled: isDisabled,
                isLinkable: isLinkable,
                binding: binding,
                isHighlighted: isSelected,
                entryId: entry.id
            }

            return item;
        }

        if (!entryBindings?.bindings.length) {
            return [createSlotForBinding()];
        }
        else {
            const slots: ReportChunkSlotItem[] = [];
            for (const binding of entryBindings.bindings) {
                if (slots.length > 0 && !binding.spanId) {
                    // Don't need to show empty slots other than the first one
                    continue;
                }

                slots.push(createSlotForBinding(binding));
            }

            return dispatch(reportChunkSlotItemReadingOrderComparerThunk(slots));
        }
    }, [entryBindings, entry.id, isDisabled, isLinkable, dispatch, isSelected]);

    return {
        slots: slots,
        isEmpty: isInitialized && slots.length === 1 && !slots[0].binding?.spanId
    }
}

export const reportChunkSlotItemReadingOrderComparerThunk = (items: ReportChunkSlotItem[]) => (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();
    const document = state.sdv.document;
    if (!!document) {
        const mappedTokens = selectMappedTokens(state, document.docsetId, document.fileId);
        return items.sort((x, y) => {
            const xSpanId = SpanIdUtils.tryParse(x.binding?.spanId);
            const ySpanId = SpanIdUtils.tryParse(y.binding?.spanId);
            return TokenRangeUtils.readingOrderComparer(SpanIdUtils.getTokenRange(mappedTokens, xSpanId), SpanIdUtils.getTokenRange(mappedTokens, ySpanId))
        })
    }

    return items;
}

export const spanIdObjectReadingOrderComparerThunk = <T extends { spanId?: string }>(items: T[]) => (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();
    const document = state.sdv.document;
    if (!!document) {
        const mappedTokens = selectMappedTokens(state, document.docsetId, document.fileId);
        return items.sort((x, y) => {
            const xSpanId = SpanIdUtils.tryParse(x.spanId);
            const ySpanId = SpanIdUtils.tryParse(y.spanId);
            return TokenRangeUtils.readingOrderComparer(SpanIdUtils.getTokenRange(mappedTokens, xSpanId), SpanIdUtils.getTokenRange(mappedTokens, ySpanId))
        })
    }

    return items;
}
